import Utils from '../../../utils/utils';
import { buildExpensePayload } from './contract-expense-json.js';

export const RULE_EFFECT_BLOCK = 'block';
export const RULE_EFFECT_WARNING = 'warning';
export const RULE_EFFECT_EXCEPTION_REQUIRED = 'exception_required';

const EMPTY_RULE_ALERTS = { blocking: [], warnings: [], exceptions: [] };

/**
 * Prepare expense json for live policy evaluation before review save persists amounts.
 * Form amount is Receipt Total (UnadjustedTotal). category_spend_limit comparison
 * basis is configured per rule (amount_basis on the rule document); the API derives
 * the comparison amount from UnadjustedTotal, Tip, and tax profile when Total is absent
 * (XPEN-2638). Web does not send stale persisted Total/TotalWithTax for live eval.
 */
export function enrichExpenseJsonForRulesEvaluation(json = {}, _data) {
  const unadjusted = parseFloat(json.UnadjustedTotal) || 0;
  const tip = parseFloat(json.Tip) || 0;
  const totalWithTax = Math.max(0, unadjusted - tip);

  return {
    ...json,
    UnadjustedTotal: unadjusted,
    TotalWithTax: totalWithTax,
    BigTotalPayed: unadjusted,
  };
}

function stripStaleTotalsFromEvalJson(json = {}) {
  const {
    Total: _staleTotal,
    TotalWithTax: _staleTotalWithTax,
    Tax: _staleTax,
    Tax1: _staleTax1,
    Tax2: _staleTax2,
    BigTotalPayed: _staleBigTotalPayed,
    ...jsonForEvaluation
  } = json;

  return jsonForEvaluation;
}

export function buildExpenseDocumentForRulesEvaluation({ expenseData, payload, data }) {
  const sourceJson = payload?.json ?? {};
  const jsonForEvaluation = stripStaleTotalsFromEvalJson(sourceJson);

  return {
    checksum: expenseData?.checksum,
    name: expenseData?.name ?? expenseData?.json?.Name,
    json: enrichExpenseJsonForRulesEvaluation(jsonForEvaluation, data),
  };
}

export function classifyPolicyIssuesByEffect(issues = []) {
  const blocking = [];
  const warnings = [];
  const exceptions = [];

  for (const issue of issues ?? []) {
    if (!issue?.message) {
      continue;
    }
    const effect = issue.effect ?? RULE_EFFECT_BLOCK;
    if (effect === RULE_EFFECT_BLOCK) {
      blocking.push(issue);
    } else if (effect === RULE_EFFECT_WARNING) {
      warnings.push(issue);
    } else if (effect === RULE_EFFECT_EXCEPTION_REQUIRED) {
      exceptions.push(issue);
    }
  }

  return { blocking, warnings, exceptions };
}

export function translateRuleViolationMessages(violations = [], readLabel) {
  return (violations ?? [])
    .filter(Boolean)
    .map((message) => {
      try {
        const translated = readLabel(message);
        return translated ?? message;
      } catch {
        return message;
      }
    });
}

export function translateRuleIssueMessages(issues = [], readLabel) {
  return (issues ?? [])
    .filter((issue) => issue?.message)
    .map((issue) => {
      try {
        const translated = readLabel(issue.message);
        return translated ?? issue.message;
      } catch {
        return issue.message;
      }
    });
}

export function shouldSuppressExceptionIssues(expenseData) {
  return expenseData?.json?.exception_approval?.status === 'approved';
}

/**
 * True when live rule evaluation has enough form state (amount + imputation).
 */
export function isReviewExpenseEvaluableForRules({ cleanedExpense, data, imputationLeafStage }) {
  const amount = parseFloat(cleanedExpense?.info?.amount);
  if (!amount || amount <= 0 || Number.isNaN(amount)) {
    return false;
  }

  if (!Utils.hasExpenseField(data, 'field_imputation_level1', false)) {
    return true;
  }

  const hasL1 =
    cleanedExpense?.imputationLevel1Id &&
    String(cleanedExpense.imputationLevel1Id).trim() !== '';
  if (!hasL1) {
    return false;
  }

  if (imputationLeafStage === 'required') {
    const hasLeaf =
      cleanedExpense?.imputationLeafId &&
      String(cleanedExpense.imputationLeafId).trim() !== '';
    return Boolean(hasLeaf);
  }

  return true;
}

export function fetchExpenseRuleViolations(fetchFn, expenseDocument) {
  const formData = new FormData();
  formData.append('expense', JSON.stringify(expenseDocument));

  return new Promise((resolve, reject) => {
    fetchFn(
      '/expenses_rules_evaluation/',
      'POST',
      formData,
      (response) => resolve(response?.data?.violations ?? []),
      (error) => reject(error)
    );
  });
}

export function fetchExpenseRuleIssues(fetchFn, expenseDocument) {
  const formData = new FormData();
  formData.append('expense', JSON.stringify(expenseDocument));

  return new Promise((resolve, reject) => {
    fetchFn(
      '/expenses_rules_evaluation/',
      'POST',
      formData,
      (response) =>
        resolve({
          issues: response?.data?.issues ?? [],
          violations: response?.data?.violations ?? [],
        }),
      (error) => reject(error)
    );
  });
}

function classifyEvaluationResponse({ issues, violations }) {
  if (issues?.length) {
    return classifyPolicyIssuesByEffect(issues);
  }

  if (violations?.length) {
    return {
      blocking: violations.map((message) => ({
        effect: RULE_EFFECT_BLOCK,
        message,
      })),
      warnings: [],
      exceptions: [],
    };
  }

  return { blocking: [], warnings: [], exceptions: [] };
}

function toRuleAlertMessages(classified, readLabel) {
  return {
    blocking: translateRuleIssueMessages(classified.blocking, readLabel),
    warnings: translateRuleIssueMessages(classified.warnings, readLabel),
    exceptions: translateRuleIssueMessages(classified.exceptions, readLabel),
  };
}

/**
 * Evaluate admin-configured expense rules against the current review form state.
 * Returns effect-aware alert messages (empty arrays when compliant).
 */
export async function evaluateExpenseReviewRuleAlerts({
  fetchFn,
  expenseData,
  cleanedExpense,
  mainInfo,
  data,
  imputationRows = null,
  readLabel,
}) {
  const payload = buildExpensePayload(cleanedExpense, expenseData, mainInfo, data, imputationRows);
  const expenseDocument = buildExpenseDocumentForRulesEvaluation({ expenseData, payload, data });
  const { issues, violations } = await fetchExpenseRuleIssues(fetchFn, expenseDocument);

  let classified = classifyEvaluationResponse({ issues, violations });

  if (shouldSuppressExceptionIssues(expenseData)) {
    classified = { ...classified, exceptions: [] };
  }

  return toRuleAlertMessages(classified, readLabel);
}

/**
 * @deprecated Prefer evaluateExpenseReviewRuleAlerts for effect-aware alerts.
 * Returns user-facing blocking violation messages only (empty when compliant).
 */
export async function evaluateExpenseReviewRuleViolations({
  fetchFn,
  expenseData,
  cleanedExpense,
  mainInfo,
  data,
  readLabel,
}) {
  const alerts = await evaluateExpenseReviewRuleAlerts({
    fetchFn,
    expenseData,
    cleanedExpense,
    mainInfo,
    data,
    readLabel,
  });
  return alerts.blocking;
}

export { EMPTY_RULE_ALERTS };
